import React, { useState, useRef, useEffect, useCallback } from "react";
import {Search as SearchIcon} from "@styled-icons/bootstrap/Search";
import {CloseOutline as CloseIcon} from "@styled-icons/evaicons-outline/CloseOutline";
import styled from 'styled-components'
import { useDispatch } from "react-redux";
import { searchItem, getMenu } from "../../redux/feature/menuSlice";
import { useMediaQuery } from "../../hooks/mediaquery";
import {b} from '../../styles/variables'
import { useLocation } from "react-router-dom";

function SearchBar({ placeholder }) {
  const [wordEntered, setWordEntered] = useState("");
  const dispatch = useDispatch();
  const isMobileView = useMediaQuery(b.mobile);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const location = useLocation();
  const params = location.pathname.split("/");

  const getMainURL = useCallback(() => {
    const url = params[2] || "foodall";
    return params.length === 4 ? url + "/" + params[3] : url;
  }, [params]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (wordEntered) {
          setWordEntered("");
          dispatch(getMenu(getMainURL()));
        }
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [wordEntered, dispatch, getMainURL]);

  const handleFilter = (event) => {
    const searchWord = event.target.value;
    setWordEntered(searchWord);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (searchWord.trim() === "") {
        dispatch(getMenu(getMainURL()));
      } else {
        dispatch(searchItem(searchWord.trim()));
      }
    }, 350);
  };

  const clearInput = () => {
    setWordEntered("");
    dispatch(getMenu(getMainURL()));
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <SearchContainer ref={wrapperRef}>
      <SearchIconWrapper>
        <SearchIcon size={isMobileView ? 16 : 18} />
      </SearchIconWrapper>
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={wordEntered}
        onChange={handleFilter}
      />
      {wordEntered && (
        <ClearButton onClick={clearInput} type="button">
          <CloseIcon size={isMobileView ? 18 : 22} />
        </ClearButton>
      )}
    </SearchContainer>
  );
}

export default SearchBar;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f4f4f5;
  border: 1.5px solid #e4e4e7;
  border-radius: 12px;
  padding: 0 14px;
  height: 44px;
  min-width: 260px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #F7707D;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(247, 112, 125, 0.1);
  }

  @media ${b.mobile} {
    min-width: 0;
    width: 100%;
    height: 44px;
    padding: 0 10px;
  }
`;

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #a1a1aa;
  margin-right: 8px;
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1.4rem;
  color: #18181b;
  font-family: 'Nunito Sans', sans-serif;

  &::placeholder {
    color: #a1a1aa;
    font-weight: 400;
  }

  @media ${b.mobile} {
    font-size: 1.3rem;
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  color: #71717a;
  padding: 10px;
  border-radius: 50%;
  margin-left: 2px;
  margin-right: -6px;
  flex-shrink: 0;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover, &:active {
    background: #e4e4e7;
    color: #18181b;
  }
`;
